import { useState } from 'react';
import axios from '../../axios';
import config from '../../config';
import { useHistory } from 'react-router-dom';
import { trackFormSubmission } from '../../utils/analytics';

const useSellerFormState = (workflowType = 'ai') => {
  const backend_url = config.backend_url;
  const history = useHistory();

  const [object, setObject] = useState('');
  const [address, setAddress] = useState('');
  const [ad, setAd] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finalSubmit, setFinalSubmit] = useState(false);
  const [nextQuestion, setNextQuestion] = useState('');
  const [questionsAnswers, setQuestionsAnswers] = useState('');
  const [questionsCount, setQuestionsCount] = useState(0);
  const [images, setImages] = useState([]);
  const [multipleImageUrls, setMultipleImageUrls] = useState([]);
  const [sellerDataId, setSellerDataId] = useState(null);
  const [isFinalQuestion, setIsFinalQuestion] = useState(false);
  const [propertyType, setPropertyType] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);

  const clearForm = async () => {
    setObject('');
    setAddress('');
    setFormSubmitted(false);
    setLoading(false);
    setFinalSubmit(false);
    setNextQuestion('');
    setQuestionsAnswers('');
    setQuestionsCount(0);
    setImages([]);
    setMultipleImageUrls([]);
    setSellerDataId(null);
    setIsFinalQuestion(false);
    setPropertyType('');
    setAiSuggestions([]);

    try {
      await axios.post(`${backend_url}/api/clear-session`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Failed to clear session:', error);
    }
  };
  
  const handleSubmit = async (formData, imageFiles, apiFormData) => {
    setLoading(true);
    
    // Store the property type in local state
    if (formData.property_type) {
      setPropertyType(formData.property_type);
    }
  
    // Store the address in local state
    if (formData.address) setAddress(formData.address);
  
    try {
      let response;
      
      // Add personnummer from localStorage if available
      const personnummer = localStorage.getItem('personnummer');
      if (personnummer && formData) {
        formData.personnummer = personnummer;
      }
      
      // If apiFormData is provided, use it directly
      if (apiFormData) {
        // Add personnummer to apiFormData if available
        if (personnummer) {
          apiFormData.append('personnummer', personnummer);
        }
        
        response = await axios.post(`${backend_url}/api/submit-object`, apiFormData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // Otherwise, build the FormData object ourselves
        const finalData = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          finalData.append(key, value);
        });
        
        // Ensure personnummer is included
        if (personnummer && !formData.personnummer) {
          finalData.append('personnummer', personnummer);
        }
    
        if (imageFiles && imageFiles.length > 0) {
          imageFiles.forEach((file) => {
            finalData.append('files[]', file);
          });
        }
    
        response = await axios.post(`${backend_url}/api/submit-object`, finalData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
  
      const data = response.data;

      if (data.seller_data_id) {
        setSellerDataId(data.seller_data_id);
      }

      // Set the next question
      if (data.next_question) {
        setNextQuestion(data.next_question);
      }
      
      // Process AI suggestions if they exist in the response
      if (data.ai_suggestions && Array.isArray(data.ai_suggestions)) {
        setAiSuggestions(data.ai_suggestions);
      } else {
        // If AI suggestions are not provided from backend, make a separate call to get them
        try {
          const suggestionsResponse = await axios.post(
            `${backend_url}/api/get-first-question-suggestions`,
            {
              property_type: formData.property_type,
              question: data.next_question,
              object: formData.object,
              seller_data_id: data.seller_data_id,
              personnummer: personnummer
            },
            { withCredentials: true }
          );
          
          if (suggestionsResponse.data.ai_suggestions && Array.isArray(suggestionsResponse.data.ai_suggestions)) {
            setAiSuggestions(suggestionsResponse.data.ai_suggestions);
          } else {
            setAiSuggestions([]); // Reset if no suggestions received
          }
        } catch (suggestionsError) {
          console.error('Error fetching AI suggestions for first question:', suggestionsError);
          setAiSuggestions([]); // Reset if error
        }
      }

      // Once form is submitted and we have a next question, mark form as submitted
      setFormSubmitted(true);

    } catch (error) {
      console.error('Error in handleSubmit:', error);
      throw error; // Re-throw the error so it can be caught by the form
    } finally {
      setLoading(false);
    }
  };
  

  const handleIntermediateFollowUpSubmit = async (answer, lastQuestion) => {
    try {
      // Get personnummer from localStorage
      const personnummer = localStorage.getItem('personnummer');
      
      const payload = {
        answer,
        last_question: lastQuestion,
        seller_data_id: sellerDataId,
        property_type: propertyType
      };
      
      // Add personnummer if available
      if (personnummer) {
        payload.personnummer = personnummer;
      }
      
      const response = await axios.post(
        `${backend_url}/api/submit-follow-up-answers`,
        payload,
        { withCredentials: true }
      );
      const data = response.data;
      // Update questionsAnswers with the response data, or keep existing if undefined
      setQuestionsAnswers(data.questions_answers || questionsAnswers);
      setNextQuestion(data.next_question);
      if (data.seller_data_id) {
        setSellerDataId(data.seller_data_id);
      }
      if (data.is_final_question) {
        setIsFinalQuestion(true);
      }
      if (data.ai_suggestions && Array.isArray(data.ai_suggestions)) {
        setAiSuggestions(data.ai_suggestions);
      } else {
        setAiSuggestions([]);
      }
      setQuestionsCount((prev) => prev + 1);
    } catch (error) {
      console.error('Error in handleIntermediateFollowUpSubmit:', error);
    }
  };

  const handleFinalFollowUpSubmit = async (answer, lastQuestion) => {
    setLoading(true);
    try {
      // Get personnummer from localStorage
      const personnummer = localStorage.getItem('personnummer');
      
      const payload = {
        answer,
        last_question: lastQuestion,
        seller_data_id: sellerDataId,
        property_type: propertyType
      };
      
      // Add personnummer if available
      if (personnummer) {
        payload.personnummer = personnummer;
      }
      
      const response = await axios.post(
        `${backend_url}/api/submit-final-followup`,
        payload,
        { withCredentials: true }
      );
      
      const data = response.data;
      if (data.final_ad) {
        setAd(data.final_ad);
      }
      
      // Track final form submission completion (main conversion event)
      trackFormSubmission('final_form_completion', {
        property_type: propertyType,
        workflow_type: 'ai',
        seller_data_id: data.seller_data_id || sellerDataId
      });
      
      setFinalSubmit(true);
      if (data.seller_data_id) {
        history.push(`/seller-data/${data.seller_data_id}`);
      } else {
        history.push('/seller-data');
      }
    } catch (error) {
      console.error('Error in handleFinalFollowUpSubmit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectSubmit = async (formData, imageFiles, apiFormData) => {
    setLoading(true);
    
    // Store the property type in local state
    if (formData.property_type) {
      setPropertyType(formData.property_type);
    }
  
    // Store the address in local state
    if (formData.address) setAddress(formData.address);
  
    try {
      let response;
      
      // Add personnummer from localStorage if available
      const personnummer = localStorage.getItem('personnummer');
      if (personnummer && formData) {
        formData.personnummer = personnummer;
      }
      
      // If apiFormData is provided, use it directly
      if (apiFormData) {
        // Add personnummer to apiFormData if available
        if (personnummer) {
          apiFormData.append('personnummer', personnummer);
        }
        
        response = await axios.post(`${backend_url}/api/submit-object-direct`, apiFormData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // Otherwise, build the FormData object ourselves
        const finalData = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          finalData.append(key, value);
        });
        
        // Ensure personnummer is included
        if (personnummer && !formData.personnummer) {
          finalData.append('personnummer', personnummer);
        }
    
        if (imageFiles && imageFiles.length > 0) {
          imageFiles.forEach((file) => {
            finalData.append('files[]', file);
          });
        }
    
        response = await axios.post(`${backend_url}/api/submit-object-direct`, finalData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
  
      const data = response.data;

      if (data.seller_data_id) {
        setSellerDataId(data.seller_data_id);
      }

      // For direct submission, mark as final submit and redirect
      setFormSubmitted(true);
      setFinalSubmit(true);
      
      // Track direct form submission completion (main conversion event)
      trackFormSubmission('direct_form_completion', {
        property_type: propertyType,
        workflow_type: 'direct',
        seller_data_id: data.seller_data_id || sellerDataId
      });
      
      if (data.seller_data_id) {
        history.push(`/seller-data/${data.seller_data_id}`);
      } else {
        history.push('/seller-data');
      }
  
    } catch (error) {
      console.error('Error in handleDirectSubmit:', error);
      throw error; // Re-throw the error so it can be caught by the form
    } finally {
      setLoading(false);
    }
  };

  return {
    backend_url,
    object,
    setObject,
    address,
    setAddress,
    ad,
    setAd,
    formSubmitted,
    setFormSubmitted,
    loading,
    setLoading,
    finalSubmit,
    setFinalSubmit,
    nextQuestion,
    setNextQuestion,
    questionsCount,
    setQuestionsCount,
    questionsAnswers,
    setQuestionsAnswers,
    images,
    setImages,
    multipleImageUrls,
    setMultipleImageUrls,
    sellerDataId,
    setSellerDataId,
    clearForm,
    handleSubmit,
    handleIntermediateFollowUpSubmit,
    handleFinalFollowUpSubmit,
    handleDirectSubmit,
    isFinalQuestion,
    setIsFinalQuestion,
    propertyType,
    setPropertyType,
    aiSuggestions
  };
};

export default useSellerFormState;
